import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import { Form } from '@unform/web';
import { isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import * as Yup from 'yup';

import { Spinner } from '~/components';
import { Input, Select, Autocomplete, Hidden } from '~/components/Form';
import {
  DESCARGA_NF,
  SOBRAS_E_FALTAS,
  CUSTOMIZACAO_RAIZEN,
  MODULO_20_GRAUS,
  MODULO_FERROVIA,
} from '~/constants/modules';
import { t, validCharacters, can } from '~/helpers';
import { storeBico, updateBico } from '~/store/modules/bico/actions';

export default function CreateOrUpdate({
  reference,
  data,
  create,
  toggleLoading,
  toggleConfirmButton,
}) {
  const dispatch = useDispatch();

  const { client } = useSelector((state) => state.user.profile);
  const { loading, find, params } = useSelector((state) => state.bico);

  const [bicoAutomatico, setBicoAutomatico] = useState('0');
  const [terminalFerrovia, setTerminalFerrovia] = useState(false);

  const [posto, setPosto] = useState(0);
  const [valueIdTerminal, setValueIdTerminal] = React.useState(0);
  const [inputValueIdTerminal, setInputValueIdTerminal] = React.useState('');
  const [motorBomba, setMotorBomba] = React.useState('0');

  const [
    bloqueiaDigitacaoMovimentacaoBicoHabCampo,
    setBloqueiaDigitacaoMovimentacaoBicoHabCampo,
  ] = React.useState('N');

  const [valvulaDuploEstagio, setValvulaDuploEstagio] = React.useState('0');
  const [
    controleLinhaAbastecimentoTerminal,
    setControleLinhaAbastecimentoTerminal,
  ] = useState(false);
  const [numeroLinhaAbastecimento, setNumeroLinhaAbastecimento] = useState(0);
  const [
    valueNumeroLinhaAbastecimento,
    setValueNumeroLinhaAbastecimento,
  ] = React.useState(0);
  const [
    inputValueNumeroLinhaAbastecimento,
    setInputValueNumeroLinhaAbastecimento,
  ] = React.useState('');
  const [tipoConcentradorTerminal, setTipoConcentradorTerminal] = useState(0);
  const [controlaLCRTerminal, setControlaLCRTerminal] = useState(0);
  const [pendingRequests, setPendingRequests] = useState(0);
  const [hasLoadingError, setHasLoadingError] = useState(false);
  const loadingErrorMessage = 's.geral.loadingerror';

  const handleAsyncLoadingChange = React.useCallback((isLoading) => {
    setPendingRequests((state) => {
      if (hasLoadingError) return 0;

      if (isLoading) return state + 1;

      return state > 0 ? state - 1 : 0;
    });
  }, [hasLoadingError]);

  const handleAsyncLoadingError = React.useCallback(() => {
    setHasLoadingError(true);
    setPendingRequests(0);
  }, []);

  useEffect(() => {
    toggleLoading(pendingRequests > 0);
  }, [pendingRequests, toggleLoading]);

  useEffect(() => {
    toggleConfirmButton(!hasLoadingError);
  }, [hasLoadingError, toggleConfirmButton]);

  useEffect(
    () => () => {
      toggleLoading(false);
      toggleConfirmButton(true);
    },
    [toggleLoading, toggleConfirmButton]
  );

  const statusSimNao = [
    { label: t('s.geral.opcoes.nao'), value: 'N' },
    { label: t('s.geral.opcoes.sim'), value: 'S' },
  ];

  const afericaoAutomaticaBicoSimNao = [
    { label: t('s.geral.opcoes.nao'), value: 'N' },
    { label: t('s.geral.opcoes.sim'), value: 'S' },
  ];

  const statusDigitaencerrantebico = [
    { label: t('s.geral.opcoes.nao'), value: 0 },
    { label: t('s.geral.opcoes.sim'), value: 1 },
  ];

  const tipoOverfil = [
    { label: 'Sem overfill', value: 0 },
    { label: 'Checkmate', value: 1 },
    { label: 'Checkmate + Sinal Atuado', value: 2 },
    { label: 'Software', value: 3 },
  ];

  const statusNumero = [
    { label: '1', value: 1 },
    { label: '2', value: 2 },
    { label: '3', value: 3 },
    { label: '4', value: 4 },
    { label: '5', value: 5 },
    { label: '6', value: 6 },
    { label: '7', value: 7 },
    { label: '8', value: 8 },
  ];

  const statusTipo = [
    { label: 'Valor', value: 0 },
    { label: 'Volume', value: 1 },
    { label: 'Não Predetermina', value: 2 },
  ];

  const tipoPulser = [
    { label: 'Simples', value: 0 },
    { label: 'Duplo', value: 1 },
    { label: 'Sem Pulser (digitação manual)', value: 2 },
  ];

  const status = [
    { label: t('s.geral.opcoes.liberado'), value: 'L' },
    { label: t('s.geral.opcoes.bloqueado'), value: 'B' },
    { label: t('s.geral.opcoes.cancelado'), value: 'C' },
  ];

  const nozzleClosingOptions = useMemo(
    () => [
      { label: '10', value: 10 },
      { label: '1', value: 1 },
      { label: '0,1', value: 0.1 },
      { label: '0,01', value: 0.01 },
    ],
    []
  );

  const statusBicoAutomatico = [
    { label: t('s.geral.opcoes.bicoautomaticopulser'), value: '0' },
    { label: t('s.geral.opcoes.bicoautomaticoconcentrador'), value: '1' },
    { label: t('s.geral.opcoes.bicoautomaticobombaeletronica'), value: '2' },
  ];

  const statusMonitoraLeituraContinuaBico = [
    { label: t('s.geral.opcoes.nao'), value: 0 },
    { label: t('s.geral.opcoes.sim'), value: 1 },
  ];

  const initials = create ? data : { ...find, ...data };

  function setFormFieldValue(name, value) {
    reference.current?.setFieldValue(name, value);
  }

  function handleBicoAutomatico(value) {
    const nextValue = String(value);
    setFormFieldValue('automaticobico', nextValue);
    setBicoAutomatico(nextValue);
  }

  async function handleSubmit(param) {
    try {
      if (hasLoadingError) {
        toast.error(t(loadingErrorMessage));
        return;
      }

      if (pendingRequests > 0) {
        toast.warning(t('s.geral.validacao.aguardecarregamento'));
        return;
      }

      const nextBicoAutomatico = String(
        param.automaticobico ?? bicoAutomatico ?? '0'
      );
      const nextMotorBomba = Number(param.motorbombabico ?? motorBomba ?? 0);
      const nextValvulaDuploEstagio = Number(
        param.valvuladuploestagiobico ?? valvulaDuploEstagio ?? 0
      );
      const nextBloqueiaDigitacao =
        param.bloqueiadigitacaomovimentacaobico ??
        bloqueiaDigitacaoMovimentacaoBicoHabCampo ??
        'N';
      const normalizedParam = {
        ...param,
        automaticobico: nextBicoAutomatico,
        motorbombabico: nextMotorBomba,
        valvuladuploestagiobico: nextValvulaDuploEstagio,
        bloqueiadigitacaomovimentacaobico: nextBloqueiaDigitacao,
        tmpdesligabombabico:
          nextMotorBomba === 1 ? param.tmpdesligabombabico : 0,
        percsubirvazaobico:
          nextValvulaDuploEstagio === 1 ? param.percsubirvazaobico : 0,
        volsubirvazaobico:
          nextValvulaDuploEstagio === 1 ? param.volsubirvazaobico : 0,
        voldescervazaobico:
          nextValvulaDuploEstagio === 1 ? param.voldescervazaobico : 100,
      };

      const schema = Yup.object().shape({
        idbico: create
          ? Yup.string()
            .required(t('s.geral.validacao.campoobrigatorio'))
            .test(
              'characters-invalid',
              t('s.configuracao.geral.naopodecontercaracterespecial'),
              (value) => validCharacters(value)
            )
          : Yup.string(),
        codigoauxiliarbico: Yup.string()
          .nullable()
          .trim()
          .max(20, t('s.geral.validacao.tamanhomaximo').replace('%s', 20))
          .test(
            'characters-invalid',
            t('s.configuracao.geral.naopodecontercaracterespecial'),
            (value) => validCharacters(value)
          ),
        codigobicocosanbico: can(CUSTOMIZACAO_RAIZEN)
          ? Yup.number()
            .required(t('s.geral.validacao.campoobrigatorio'))
            .typeError(t('s.geral.validacao.campoobrigatorio'))
            .min(0, t('s.geral.validacao.valorminimo').replace('%s', 0))
          : Yup.string().nullable(),
        codigobombacosanbico: can(CUSTOMIZACAO_RAIZEN)
          ? Yup.number()
            .required(t('s.geral.validacao.campoobrigatorio'))
            .typeError(t('s.geral.validacao.campoobrigatorio'))
            .min(0, t('s.geral.validacao.valorminimo').replace('%s', 0))
          : Yup.string().nullable(),
        idposto: create
          ? Yup.number().required(t('s.geral.validacao.campoobrigatorio'))
          : Yup.string(),
        idproduto:
          can(SOBRAS_E_FALTAS) || can(DESCARGA_NF)
            ? Yup.string().nullable()
            : Yup.number().required(t('s.geral.validacao.campoobrigatorio')),
        idtanquevirtual:
          can(SOBRAS_E_FALTAS) || can(DESCARGA_NF)
            ? Yup.string()
              .nullable()
              .required(t('s.geral.validacao.campoobrigatorio'))
            : Yup.string().nullable(),
        idmedidor: Yup.number().required(
          t('s.geral.validacao.campoobrigatorio')
        ),
        idterminal: Yup.string().required(
          t('s.geral.validacao.campoobrigatorio')
        ),
        vazaominimabico: Yup.number()
          .min(0, 'O valor desse campo não pode ser menor que 0')
          .max(100, 'O valor desse campo não pode ser maior que 100')
          .nullable(),
        fatorsegurancabico: Yup.number()
          .min(0, 'O valor desse campo não pode ser menor que 0')
          .max(100, 'O valor desse campo não pode ser maior que 100')
          .nullable(),
        timeoutbico: Yup.number()
          .required(t('s.geral.validacao.campoobrigatorio'))
          .max(
            65535,
            t('s.geral.validacao.tamanhomaximo').replace('%s', 65535)
          ),
        tmpdesligabombabico:
          nextMotorBomba === 1
            ? Yup.number()
              .typeError(t('s.geral.validacao.campoobrigatorio'))
              .min(0, t('s.geral.validacao.tamanhominimo').replace('%s', 1))
              .max(
                65535,
                t('s.geral.validacao.tamanhomaximo').replace('%s', 65535)
              )
            : Yup.number().nullable(),
        percsubirvazaobico:
          nextValvulaDuploEstagio === 1
            ? Yup.number()
              .typeError(t('s.geral.validacao.campoobrigatorio'))
              .min(0, t('s.geral.validacao.tamanhominimo').replace('%s', 1))
              .max(
                65535,
                t('s.geral.validacao.tamanhomaximo').replace('%s', 65535)
              )
            : Yup.number().nullable(),
        volsubirvazaobico:
          nextValvulaDuploEstagio === 1
            ? Yup.number()
              .typeError(t('s.geral.validacao.campoobrigatorio'))
              .min(0, t('s.geral.validacao.tamanhominimo').replace('%s', 1))
              .max(
                65535,
                t('s.geral.validacao.tamanhomaximo').replace('%s', 65535)
              )
            : Yup.number().nullable(),
        voldescervazaobico:
          nextValvulaDuploEstagio === 1
            ? Yup.number()
              .typeError(t('s.geral.validacao.campoobrigatorio'))
              .min(0, t('s.geral.validacao.tamanhominimo').replace('%s', 1))
              .max(
                65535,
                t('s.geral.validacao.tamanhomaximo').replace('%s', 65535)
              )
            : Yup.number().nullable(),
        timeoutvazamentobico: Yup.number()
          .required(t('s.geral.validacao.campoobrigatorio'))
          .max(
            65535,
            t('s.geral.validacao.tamanhomaximo').replace('%s', 65535)
          ),
        vazaominimavazbico: Yup.number()
          .required(t('s.geral.validacao.campoobrigatorio'))
          .max(
            65535,
            t('s.geral.validacao.tamanhomaximo').replace('%s', 65535)
          ),
        volumeminimovazbico: Yup.number()
          .required(t('s.geral.validacao.campoobrigatorio'))
          .max(
            65535,
            t('s.geral.validacao.tamanhomaximo').replace('%s', 65535)
          ),
        automaticobico: Yup.string()
          .required(t('s.geral.validacao.campoobrigatorio'))
          .typeError(t('s.geral.validacao.campoobrigatorio'))
          .test(
            'verifica-semconcentrador',
            t('s.configuracao.bico.terminalsemconcentrador'),
            () =>
              !(nextBicoAutomatico === '1' && tipoConcentradorTerminal === 0)
          )
          .test(
            'verifica-sembombaeletronica',
            t('s.configuracao.bico.terminalsembombaeletronica'),
            () => !(nextBicoAutomatico === '2' && controlaLCRTerminal === 0)
          ),
        diasliberadigitacaomovimentacaobico: Yup.number()
          .required(t('s.geral.validacao.campoobrigatorio'))
          .min(1, t('s.geral.validacao.valorminimo').replace('%s', 1))
          .max(65535, t('s.geral.validacao.valormaximo').replace('%s', 65535)),
        habilitatrocatanque: Yup.string()
          .required(t('s.geral.validacao.campoobrigatorio'))
          .typeError(t('s.geral.validacao.campoobrigatorio')),
        monitorainclinacaodbicbico: Yup.string()
          .required(t('s.geral.validacao.campoobrigatorio'))
          .max(1, t('s.geral.validacao.tamanhomaximo').replace('%s', 1)),
        statusbico: Yup.string(),
        tipopulserbico: Yup.string(),
        stoplcrbico:
          nextBicoAutomatico === '2' // Bomba eletronica (LCR)
            ? Yup.string()
              .typeError(t('s.geral.validacao.campoobrigatorio'))
              .required(t('s.geral.validacao.campoobrigatorio'))
            : Yup.string().nullable(),
        canalcbcbico:
          tipoConcentradorTerminal === 1 // Concentrador CBC
            ? Yup.number()
              .max(8, t('s.geral.validacao.tamanhomaximo').replace('%s', 8))
              .min(1, t('s.geral.validacao.tamanhominimo').replace('%s', 1))
            : Yup.string(),
        enderecocbcbico:
          tipoConcentradorTerminal === 1 || nextBicoAutomatico === '2' // Concentrador CBC ou bomba eletronica (LCR)
            ? Yup.number().max(
              65535,
              t('s.geral.validacao.tamanhomaximo').replace('%s', 65535)
            )
            : Yup.string(),
        numeroladocbcbico:
          tipoConcentradorTerminal === 1 // Concentrador CBC
            ? Yup.number().max(
              65535,
              t('s.geral.validacao.tamanhomaximo').replace('%s', 65535)
            )
            : Yup.string(),
        casasvalortotalcbcbico:
          nextBicoAutomatico !== '0'
            ? Yup.number().max(
              65535,
              t('s.geral.validacao.tamanhomaximo').replace('%s', 65535)
            )
            : Yup.string(),
        casasvalorlitrocbcbico:
          nextBicoAutomatico !== '0'
            ? Yup.number().max(
              65535,
              t('s.geral.validacao.tamanhomaximo').replace('%s', 65535)
            )
            : Yup.string(),
        casasvalorunitariocbcbico:
          nextBicoAutomatico !== '0'
            ? Yup.number().max(
              65535,
              t('s.geral.validacao.tamanhomaximo').replace('%s', 65535)
            )
            : Yup.string(),
        tempoligabombabico: Yup.number()
          .required(t('s.geral.validacao.campoobrigatorio'))
          .max(
            65535,
            t('s.geral.validacao.tamanhomaximo').replace('%s', 65535)
          ),
        exibevolumebico: Yup.string(),
        digitosbico: Yup.number()
          .required(t('s.geral.validacao.campoobrigatorio'))
          .max(
            65535,
            t('s.geral.validacao.tamanhomaximo').replace('%s', 65535)
          ),
        numerobombasbico: Yup.number()
          .required(t('s.geral.validacao.campoobrigatorio'))
          .max(
            65535,
            t('s.geral.validacao.tamanhomaximo').replace('%s', 65535)
          ),
        tipopredeterminacaobico: Yup.string(),
        digitaencerrantebico: Yup.string(),
        multencerhrsbico: Yup.number()
          .required(t('s.geral.validacao.campoobrigatorio'))
          .typeError(t('s.geral.validacao.campoobrigatorio')),
        multpredethrsbico: Yup.number()
          .required(t('s.geral.validacao.campoobrigatorio'))
          .typeError(t('s.geral.validacao.campoobrigatorio')),
        tipotemperaturabico: can(MODULO_20_GRAUS)
          ? Yup.number()
            .required(t('s.geral.validacao.campoobrigatorio'))
            .typeError(t('s.geral.validacao.campoobrigatorio'))
          : Yup.number(),
        vazaomaximabico:
          can(MODULO_FERROVIA) && controleLinhaAbastecimentoTerminal
            ? Yup.number()
              .required(t('s.geral.validacao.campoobrigatorio'))
              .min(0, t('s.geral.validacao.tamanhominimo').replace('%s', 1))
              .max(
                65535,
                t('s.geral.validacao.tamanhomaximo').replace('%s', 65535)
              )
            : Yup.number(),
        // numerolinhaabastecimento: controleLinhaAbastecimentoTerminal
        //   ? Yup.number()
        //       .required(t('s.geral.validacao.campoobrigatorio'))
        //       .typeError(t('s.geral.validacao.campoobrigatorio'))
        //       .min(0, t('s.geral.validacao.tamanhominimo').replace('%s', 1))
        //       .max(
        //         65535,
        //         t('s.geral.validacao.tamanhomaximo').replace('%s', 65535)
        //       )
        //   : Yup.number().nullable(),
      });

      await schema.validate(normalizedParam, {
        abortEarly: false,
      });

      const objSend = {
        idtanquevirtual: null,
        // tmpdesligabombabico: 0,
        // percsubirvazaobico: 0,
        // volsubirvazaobico: 0,
        // voldescervazaobico: 100,
        // tipooverfillbico: 0,
        ...initials,
        ...normalizedParam,
        idproduto: Number(normalizedParam.idproduto),
        idcliente: client,
        automaticobico: nextBicoAutomatico,
        motorbombabico: nextMotorBomba,

        bloqueiadigitacaomovimentacaobico: nextBloqueiaDigitacao,

        valvuladuploestagiobico: nextValvulaDuploEstagio,
        tipotemperaturabico: can(MODULO_20_GRAUS)
          ? normalizedParam.tipotemperaturabico
          : 0,
        numerolinhaabastecimento: numeroLinhaAbastecimento,
        canalcbcbico:
          nextBicoAutomatico === '0' // Pulser
            ? 0
            : nextBicoAutomatico === '1' && tipoConcentradorTerminal === 1 // Concentrador CBC
              ? normalizedParam.canalcbcbico
              : nextBicoAutomatico === '1' && tipoConcentradorTerminal > 1 // Concentrador ou Horustech ou Abastek
                ? 1
                : nextBicoAutomatico === '2' && controlaLCRTerminal === 1 // Bomba eletronica (LCR)
                  ? 9
                  : 0,
        enderecocbcbico:
          nextBicoAutomatico === '0' // Pulser
            ? 0
            : nextBicoAutomatico === '1' && tipoConcentradorTerminal == 1 // Concentrador CBC
              ? normalizedParam.enderecocbcbico
              : nextBicoAutomatico === '1' && tipoConcentradorTerminal > 1 // Concentrador ou Horustech ou Abastek
                ? 1
                : nextBicoAutomatico === '2' && controlaLCRTerminal === 1 // Bomba eletronica (LCR)
                  ? normalizedParam.enderecocbcbico
                  : 0,
        numeroladocbcbico:
          nextBicoAutomatico === '0' // Pulser
            ? 0
            : nextBicoAutomatico === '1' && tipoConcentradorTerminal == 1 // Concentrador CBC
              ? normalizedParam.numeroladocbcbico
              : nextBicoAutomatico === '1' && tipoConcentradorTerminal > 1 // Concentrador ou Horustech ou Abastek
                ? 1
                : nextBicoAutomatico === '2' && controlaLCRTerminal === 1 // Bomba eletronica (LCR)
                  ? 1
                  : 0,
        stoplcrbico:
          nextBicoAutomatico === '2' ? normalizedParam.stoplcrbico : 0, // Bomba eletronica (LCR)
      };

      if (create) {
        await dispatch(storeBico(objSend, params));
      } else {
        await dispatch(updateBico(objSend, params));
      }
    } catch (err) {
      const validationErrors = {};

      if (err instanceof Yup.ValidationError) {
        err.inner.forEach((error) => {
          validationErrors[error.path] = error.message.replace(
            `${error.path} `,
            ''
          );
        });

        reference.current.setErrors(validationErrors);
      }
    }
  }

  useEffect(() => {
    if (!create && find.automaticobico) {
      setBicoAutomatico(find.automaticobico || '0');
    }

    if (!create && find.bloqueiadigitacaomovimentacaobico) {
      setBloqueiaDigitacaoMovimentacaoBicoHabCampo(
        find.bloqueiadigitacaomovimentacaobico || 'N'
      );
    }

    if (create) {
      setBloqueiaDigitacaoMovimentacaoBicoHabCampo('S');
    }
  }, [find, create]);

  return (
    <>
      {loading ? (
        <div style={{ height: '14rem', width: '50rem' }}>
          <Spinner loading />
        </div>
      ) : hasLoadingError ? (
        <div
          style={{
            border: '1px solid #f5c6cb',
            background: '#f8d7da',
            color: '#721c24',
            borderRadius: '4px',
            padding: '16px',
            lineHeight: 1.5,
          }}
        >
          {t(loadingErrorMessage)}
        </div>
      ) : (
        <div style={{ position: 'relative', minHeight: '14rem' }}>
          <Form ref={reference} initialData={initials} onSubmit={handleSubmit}>
            <Autocomplete
              name="idposto"
              label="codigonomeposto"
              url="postoscomboios"
              unique="idposto"
              disabled={!create}
              title={t('s.geral.postocomboio')}
              getValue={(value) => {
                // if (JSON.stringify(value) === '{}') {

                setValueIdTerminal(0);
                setInputValueIdTerminal('');

                setNumeroLinhaAbastecimento(0);
                setValueNumeroLinhaAbastecimento(0);
                setInputValueNumeroLinhaAbastecimento('');

                if (value.idposto === null) {
                  setPosto(0);
                } else {
                  setPosto(value.idposto);
                }
              }}
              onLoadingChange={handleAsyncLoadingChange}
              onLoadingError={handleAsyncLoadingError}
            />

            <Input
              name="idbico"
              label={t('s.configuracao.bico.idbico')}
              type="number"
              disabled={!create}
            />

            <Input
              name="codigoauxiliarbico"
              label={t('s.configuracao.bico.codigoauxiliarbico')}
            />

            {can(CUSTOMIZACAO_RAIZEN) && (
              <>
                <Input
                  name="codigobicocosanbico"
                  type="number"
                  label={t('s.configuracao.bico.codigobicocosanbico')}
                />

                <Input
                  name="codigobombacosanbico"
                  type="number"
                  label={t('s.configuracao.bico.codigobombacosanbico')}
                />
              </>
            )}

            {can(SOBRAS_E_FALTAS) || can(DESCARGA_NF) ? (
              <Autocomplete
                name="idtanquevirtual"
                label="tanquevirtualprodutotanquevirtual"
                url="tanquesvirtuais"
                unique="idtanquevirtual"
                title={t('s.configuracao.bico.idtanquevirtual')}
                hiddens={[{ name: 'idproduto' }]}
                disabled={posto === 0}
                params={{
                  wherefield: 'idposto',
                  valuewherefield: isEmpty(find) ? posto : find.idposto,
                  typewherefield: 'EQUALS',
                }}
                onLoadingChange={handleAsyncLoadingChange}
                onLoadingError={handleAsyncLoadingError}
              />
            ) : (
              <Autocomplete
                name="idproduto"
                label="nomeproduto"
                url="produtos"
                unique="idproduto"
                title={t('s.menu.configuracao.produto')}
                onLoadingChange={handleAsyncLoadingChange}
                onLoadingError={handleAsyncLoadingError}
              />
            )}

            <Autocomplete
              name="idmedidor"
              label="descricaomedidor"
              url="medidores"
              unique="idmedidor"
              title={t('s.menu.configuracao.medidor')}
              onLoadingChange={handleAsyncLoadingChange}
              onLoadingError={handleAsyncLoadingError}
            />

            {posto !== 0 && (
              <Autocomplete
                id="idterminal"
                name="idterminal"
                label="idterminal"
                url="terminais"
                unique="idterminal"
                title={t('s.menu.configuracao.terminal')}
                disabled={posto === 0}
                value={valueIdTerminal}
                inputValue={inputValueIdTerminal}
                onChange={(event, newValue) => {
                  setValueIdTerminal(newValue);
                }}
                onInputChange={(event, newInputValueIdTerminal) => {
                  setInputValueIdTerminal(newInputValueIdTerminal);
                }}
                getValue={(value) => {
                  if (value.tipoterminal !== undefined) {
                    setTerminalFerrovia(value.tipoterminal === 4); // Terminal Ferroviario
                  }

                  if (value?.controlelinhaabastecimentoterminal !== undefined) {
                    setControleLinhaAbastecimentoTerminal(
                      value.controlelinhaabastecimentoterminal == 1 || // Remoto
                      value.controlelinhaabastecimentoterminal == 2 // Local
                    ); // Com controle de vazão
                  }

                  setNumeroLinhaAbastecimento(0);
                  setValueNumeroLinhaAbastecimento(0);
                  setInputValueNumeroLinhaAbastecimento('');
                  setTipoConcentradorTerminal(value.tipoconcentrador);
                  setControlaLCRTerminal(value.controlalcterminal);
                }}
                params={{
                  wherefield: 'idposto',
                  valuewherefield: posto || find.idposto,
                  typewherefield: 'EQUALS',
                  fieldlist:
                    'idterminal,tipoterminal,tipoconcentrador,controlalcterminal,controlelinhaabastecimentoterminal',
                }}
                onLoadingChange={handleAsyncLoadingChange}
                onLoadingError={handleAsyncLoadingError}
              />
            )}

            {controleLinhaAbastecimentoTerminal && (
              <>
                <Autocomplete
                  id="numerolinhaabastecimento"
                  name="numerolinhaabastecimento"
                  label="descricaolinhaabastecimento"
                  url="linhasabastecimentos"
                  unique="numerolinhaabastecimento"
                  title={t('s.menu.configuracao.linhasabastecimentos')}
                  disabled={posto === 0}
                  value={valueNumeroLinhaAbastecimento}
                  inputValue={inputValueNumeroLinhaAbastecimento}
                  onChange={(event, newValue) => {
                    setValueNumeroLinhaAbastecimento(newValue);
                  }}
                  onInputChange={(
                    event,
                    newInputValueNumeroLinhaAbastecimento
                  ) => {
                    setInputValueNumeroLinhaAbastecimento(
                      newInputValueNumeroLinhaAbastecimento
                    );
                  }}
                  getValue={(value) => {
                    if (value.numerolinhaabastecimento) {
                      setNumeroLinhaAbastecimento(value.numerolinhaabastecimento);
                    } else {
                      setNumeroLinhaAbastecimento(0);
                    }
                  }}
                  params={{
                    wherefield: 'idposto',
                    valuewherefield: posto || find.idposto,
                    typewherefield: 'EQUALS',
                    fieldlist:
                      'numerolinhaabastecimento,descricaolinhaabastecimento',
                  }}
                  onLoadingChange={handleAsyncLoadingChange}
                  onLoadingError={handleAsyncLoadingError}
                />

                <Input
                  name="vazaominimabico"
                  label={t('s.configuracao.bico.vazaominimabico')}
                  type="number"
                  inputProps={{ min: 0, max: 100 }}
                  disabled={!numeroLinhaAbastecimento}
                />

                <Input
                  name="fatorsegurancabico"
                  label={t('s.configuracao.bico.fatorsegurancabico')}
                  type="number"
                  inputProps={{ min: 0, max: 100 }}
                  disabled={!numeroLinhaAbastecimento}
                />
              </>
            )}

            {!controleLinhaAbastecimentoTerminal && (
              <>
                <Hidden name="numerolinhaabastecimento" value="0" />
                <Hidden name="vazaominimabico" value="0" />
                <Hidden name="fatorsegurancabico" value="0" />
              </>
            )}

            <Input
              name="timeoutbico"
              label={t('s.configuracao.bico.timeoutbico')}
              type="number"
            />

            <Input
              name="timeoutvazamentobico"
              label={t('s.configuracao.bico.timeoutvazamentobico')}
              type="number"
            />

            <Input
              name="vazaominimavazbico"
              label={t('s.configuracao.bico.vazaominimavazbico')}
              type="number"
            />

            <Input
              name="volumeminimovazbico"
              label={t('s.configuracao.bico.volumeminimovazbico')}
              type="number"
            />

            <Select
              name="automaticobico"
              label={t('s.configuracao.bico.bombaeletronica')}
              options={statusBicoAutomatico}
              onChange={({ target }) => handleBicoAutomatico(target.value)}
              defaultValue={create ? '0' : initials.automaticobico}
            />

            {can(SOBRAS_E_FALTAS) && bicoAutomatico === '0' && (
              <Select
                name="preencheencerranteeletronicoturnobico"
                label={t(
                  's.configuracao.bico.preencheencerranteeletronicoturnobico'
                )}
                options={statusSimNao}
                defaultValue={
                  create ? 'N' : initials.preencheencerranteeletronicoturnobico
                }
              />
            )}

            {bicoAutomatico === '1' &&
              tipoConcentradorTerminal === 1 && ( // Concentrador CBC
                <>
                  <Input
                    name="canalcbcbico"
                    label={t('s.configuracao.bico.canalcbcbico')}
                    type="number"
                    defaultValue={initials.canalcbcbico || 0}
                  />

                  <Input
                    name="enderecocbcbico"
                    label={t('s.configuracao.bico.enderecocbcbico')}
                    type="number"
                    defaultValue={initials.enderecocbcbico || 0}
                  />

                  <Input
                    name="numeroladocbcbico"
                    label={t('s.configuracao.bico.numeroladocbcbico')}
                    type="number"
                    defaultValue={initials.numeroladocbcbico || 0}
                  />
                </>
              )}

            {bicoAutomatico !== '0' && (
              <>
                <Input
                  name="casasvalortotalcbcbico"
                  label={t('s.configuracao.bico.casasvalortotalcbcbico')}
                  type="number"
                  defaultValue={initials.casasvalortotalcbcbico || 0}
                />

                <Input
                  name="casasvalorlitrocbcbico"
                  label={t('s.configuracao.bico.casasvalorlitrocbcbico')}
                  type="number"
                  defaultValue={initials.casasvalorlitrocbcbico || 0}
                />

                <Input
                  name="casasvalorunitariocbcbico"
                  label={t('s.configuracao.bico.casasvalorunitariocbcbico')}
                  type="number"
                  defaultValue={initials.casasvalorunitariocbcbico || 0}
                />
              </>
            )}

            {bicoAutomatico === '2' && ( // Bomba eletronica (LCR)
              <>
                <Input
                  name="enderecocbcbico"
                  label={t('s.configuracao.bico.enderecolcrbico')}
                  type="number"
                  defaultValue={initials.enderecocbcbico || 0}
                />

                <Select
                  name="stoplcrbico"
                  label={t('s.configuracao.bico.stoplcrbico')}
                  options={statusDigitaencerrantebico}
                />
              </>
            )}

            <Input
              name="tempoligabombabico"
              label={t('s.configuracao.bico.tempoligabombabico')}
              type="number"
            />

            <Select
              name="exibevolumebico"
              label={t('s.configuracao.bico.exibevolumebico')}
              options={statusSimNao}
              defaultValue={initials.exibevolumebico || 'N'}
            />

            <Select
              name="afericaoautomaticabico"
              label={t('s.geral.afericaoautomatica')}
              options={afericaoAutomaticaBicoSimNao}
              defaultValue={initials.afericaoautomaticabico || 'S'}
            />

            <Input
              name="digitosbico"
              label={t('s.configuracao.bico.digitosbico')}
              type="number"
            />

            <Select
              name="numerobombasbico"
              label={t('s.configuracao.bico.numerobombasbico')}
              options={statusNumero}
              defaultValue={initials.numerobombasbico || 1}
            />

            <Select
              name="tipopredeterminacaobico"
              label={t('s.configuracao.bico.tipopredeterminacaobico')}
              options={statusTipo}
              defaultValue={initials.tipopredeterminacaobico || 0}
            />

            <Select
              name="digitaencerrantebico"
              label={t('s.configuracao.bico.digitaencerrantebico')}
              options={statusDigitaencerrantebico}
              defaultValue={initials.digitaencerrantebico || 0}
            />

            <Select
              name="multencerhrsbico"
              label={t('s.configuracao.bico.multencerhrsbico')}
              options={nozzleClosingOptions}
            />

            <Select
              name="multpredethrsbico"
              label={t('s.configuracao.bico.multpredethrsbico')}
              options={nozzleClosingOptions}
            />

            <Select
              name="tipopulserbico"
              label={t('s.configuracao.bico.tipopulserbico')}
              options={tipoPulser}
              defaultValue={initials.tipopulserbico || '0'}
            />

            <Select
              name="monitoraleituracontinuadbicbico"
              label={t('s.configuracao.bico.monitoraleituracontinuadbicbico')}
              options={statusMonitoraLeituraContinuaBico}
            />

            <Select
              name="monitorainclinacaodbicbico"
              label={t('s.configuracao.bico.monitorainclinacaodbicbico')}
              options={[
                { value: 0, label: t('s.geral.nivelseguranca.semseguranca') },
                { value: 1, label: t('s.geral.nivelseguranca.comseguranca') },
              ]}
            />

            <Select
              name="bloqueiadigitacaomovimentacaobico"
              label={t('s.configuracao.bico.bloqueiadigitacaomovimentacaobico')}
              options={statusSimNao}
              defaultValue={
                create ? 'S' : initials.bloqueiadigitacaomovimentacaobico
              }
              getValue={(value) => {
                setBloqueiaDigitacaoMovimentacaoBicoHabCampo(value);
              }}
              onChange={({ target }) => {
                setFormFieldValue(
                  'bloqueiadigitacaomovimentacaobico',
                  target.value
                );
                setBloqueiaDigitacaoMovimentacaoBicoHabCampo(target.value);
              }}
            />

            {bloqueiaDigitacaoMovimentacaoBicoHabCampo === 'S' ? (
              <Input
                name="diasliberadigitacaomovimentacaobico"
                label={t(
                  's.configuracao.bico.diasliberadigitacaomovimentacaobico'
                )}
                type="number"
                defaultValue={initials.diasliberadigitacaomovimentacaobico || 1}
              />
            ) : (
              <Hidden name="diasliberadigitacaomovimentacaobico" value="1" />
            )}

            <Select
              name="habilitatrocatanque"
              label={t('s.configuracao.bico.habilitatrocatanque')}
              options={statusDigitaencerrantebico}
            />

            {can(MODULO_FERROVIA) && terminalFerrovia && (
              <>
                <Select
                  name="sinalhomemmortobico"
                  label={t('s.geral.sinalhomemmorto')}
                  options={statusDigitaencerrantebico}
                  defaultValue={initials.sinalhomemmortobico || 0}
                />
                <Select
                  name="sinalvalvulaabertabico"
                  label={t('s.geral.sinalvalvulaaberta')}
                  options={statusDigitaencerrantebico}
                  defaultValue={initials.sinalvalvulaabertabico || 0}
                />
                <Input
                  name="vazaomaximabico"
                  label={t('s.operacao.abastecimento.vazaomaximamovimentacao')}
                  type="number"
                />
              </>
            )}

            {can(MODULO_20_GRAUS) && (
              <Select
                name="tipotemperaturabico"
                label={t('s.configuracao.bico.tipotemperaturabico')}
                options={[
                  {
                    value: 0,
                    label: t(
                      's.configuracao.bico.tipotemperaturabico.semtemperatura'
                    ),
                  },
                  {
                    value: 1,
                    label: t(
                      's.configuracao.bico.tipotemperaturabico.manualdigitada'
                    ),
                  },
                  {
                    value: 2,
                    label: t('s.configuracao.bico.tipotemperaturabico.pt100'),
                  },
                  {
                    value: 3,
                    label: t('s.configuracao.bico.tipotemperaturabico.tanque'),
                  },
                ]}
              />
            )}

            <Select
              name="motorbombabico"
              label={t('s.configuracao.bico.motorbombabico')}
              options={statusDigitaencerrantebico}
              defaultValue={create ? '0' : initials.motorbombabico}
              onChange={({ target }) => {
                setFormFieldValue('motorbombabico', target.value);
                setMotorBomba(target.value);
              }}
              getValue={(value) => {
                setMotorBomba(value);
              }}
            />

            {motorBomba == '1' && (
              <Input
                type="number"
                name="tmpdesligabombabico"
                label={t('s.configuracao.bico.tmpdesligabombabico')}
                defaultValue={create ? '0' : initials.tmpdesligabombabico}
              />
            )}

            <Select
              name="valvuladuploestagiobico"
              label={t('s.configuracao.bico.valvuladuploestagiobico')}
              options={statusDigitaencerrantebico}
              defaultValue={create ? '0' : initials.valvuladuploestagiobico}
              onChange={({ target }) => {
                setFormFieldValue('valvuladuploestagiobico', target.value);
                setValvulaDuploEstagio(target.value);
              }}
              getValue={(value) => {
                setValvulaDuploEstagio(value);
              }}
            />

            {valvulaDuploEstagio == '1' && (
              <>
                <Input
                  type="number"
                  name="percsubirvazaobico"
                  label={t('s.configuracao.bico.percsubirvazaobico')}
                  defaultValue={create ? '0' : initials.percsubirvazaobico}
                />

                <Input
                  type="number"
                  name="volsubirvazaobico"
                  label={t('s.configuracao.bico.volsubirvazaobico')}
                  defaultValue={create ? '0' : initials.volsubirvazaobico}
                />

                <Input
                  type="number"
                  name="voldescervazaobico"
                  label={t('s.configuracao.bico.voldescervazaobico')}
                  defaultValue={create ? '100' : initials.voldescervazaobico}
                />
              </>
            )}

            <Select
              name="tipooverfillbico"
              label={t('s.configuracao.bico.tipooverfillbico')}
              options={tipoOverfil}
              defaultValue={create ? '0' : initials.tipooverfillbico}
            />

            <Select
              name="statusbico"
              label={t('s.configuracao.bico.statusbico')}
              options={status}
              defaultValue={initials.statusbico || 'L'}
            />
          </Form>
          <Spinner loading={pendingRequests > 0} />
        </div>
      )}
    </>
  );
}

CreateOrUpdate.propTypes = {
  reference: PropTypes.objectOf(PropTypes.any).isRequired,
  create: PropTypes.bool.isRequired,
  data: PropTypes.objectOf(PropTypes.any),
  toggleLoading: PropTypes.func,
  toggleConfirmButton: PropTypes.func,
};

CreateOrUpdate.defaultProps = {
  data: {},
  toggleLoading: () => { },
  toggleConfirmButton: () => { },
};


